import React, { useState, useEffect, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { useOfferFormContext } from "./OfferFormContext";
import { scrollToFirstError } from "../../../utils/utilFunctions";
import { getOfferEditProgressSaveFlags } from "./offerApprovalHelpers";
import TravelIcon from "../../../Assets/homepageicons/travel.svg";
import HealthIcon from "../../../Assets/homepageicons/health.svg";
import RetailIcon from "../../../Assets/homepageicons/retail.svg";
import ServicesIcon from "../../../Assets/homepageicons/services.svg";
import ActivitiesIcon from "../../../Assets/homepageicons/activities.svg";
import FoodAndBeverageIcon from "../../../Assets/homepageicons/food&beverage.svg";
import OnlineIcon from "../../../Assets/homepageicons/online.svg";

const normalizeCategoryTitle = (title) => {
  if (!title) return "";
  return String(title).toLowerCase().trim();
};

const getCategoryId = (value) => {
  if (!value) return "";
  if (typeof value === "object") {
    return value._id || value.id || "";
  }
  return value;
};

const normalizeSubCategoryIds = (items = []) =>
  items
    .map((item) => {
      if (!item) return "";
      if (typeof item === "object") {
        return item._id || item.id || "";
      }
      return item;
    })
    .filter(Boolean);

const localCategoryIconMap = {
  travel: TravelIcon,
  health: HealthIcon,
  "health & wellbeing": HealthIcon,
  "health and wellbeing": HealthIcon,
  retail: RetailIcon,
  services: ServicesIcon,
  activity: ActivitiesIcon,
  activities: ActivitiesIcon,
  "food & beverage": FoodAndBeverageIcon,
  "food and beverage": FoodAndBeverageIcon,
  online: OnlineIcon,
  "online offers": OnlineIcon,
};

const getCategoryIconSrc = (imagePath, title) => {
  const normalizedTitle = normalizeCategoryTitle(title);
  const localIcon = localCategoryIconMap[normalizedTitle];
  if (localIcon) return localIcon;

  if (!imagePath) return "";

  const baseUrl =
    process.env.REACT_APP_ICON_URL ||
    process.env.REACT_APP_IMG_URL ||
    process.env.REACT_APP_S3_URL ||
    "";

  if (/^https?:\/\//i.test(imagePath) || imagePath.startsWith("data:")) {
    return imagePath;
  }

  if (!baseUrl) {
    return imagePath;
  }

  const normalizedBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
  const normalizedPath = imagePath.startsWith("/") ? imagePath : `/${imagePath}`;

  return `${normalizedBase}${normalizedPath}`;
};

// Version: 1.1 - Compact layout and relative positioning to avoid container growth
const CategorySelection = ({
  setSubCategories,
  stepIndex,
  setStepIndex,
  categoryId,
  setCategoryId,
  setPayloadData,
  categories,
  editOfferData,
  isEdit,
  isApproved: isApprovedProp,
  handleSaveAndExit,
  payload,
  validationIssue,
  onValidationIssueHandled,
}) => {
  const { registerNextAction, setNextLabel, setIsNextDisabled } = useOfferFormContext();
  const [selectedCategory, setSelectedCategory] = useState(null);
  // B10 fix: Hold the error from a Save & Exit validation failure in a separate
  // state so it survives formik's enableReinitialize resetting touched/errors
  // when the 'categoryId' prop changes after the error was applied.
  const [externalCategoryError, setExternalCategoryError] = useState(null);
  const role = useSelector((state) => state?.authReducer?.tokenData?.user?.role);
  const offerData = useSelector((state) => state?.offerReducer?.createdOffer);

  useEffect(() => {
    setNextLabel("Next");
  }, [setNextLabel]);

  // Initial data loading
  useEffect(() => {
    if (categories?.length > 0) {
      // Priority 1: Current session payload (important for Back button)
      if (payload?.category && !selectedCategory) {
        const cat = categories.find((c) => c._id === payload.category);
        if (cat) {
          setSelectedCategory(cat);
          setCategoryId(cat._id);
          if (Array.isArray(cat.subCategory)) {
            setSubCategories(cat.subCategory);
          }
        }
      }
      // Priority 2: Initial edit data
      else if (editOfferData?.category && !selectedCategory) {
        // Handle both populated object and ID string
        const targetId = typeof editOfferData.category === 'object'
          ? editOfferData.category._id
          : editOfferData.category;

        const cat = categories.find((c) => c._id === targetId);
        if (cat) {
          setSelectedCategory(cat);
          setCategoryId(cat._id);
          if (Array.isArray(cat.subCategory)) {
            setSubCategories(cat.subCategory);
          }
        }
      }
      // Priority 3: Component state categoryId
      else if (categoryId && !selectedCategory) {
        const cat = categories.find((c) => c._id === categoryId);
        if (cat) {
          setSelectedCategory(cat);
          if (Array.isArray(cat.subCategory)) {
            setSubCategories(cat.subCategory);
          }
        }
      }
    }
  }, [categories, editOfferData, categoryId, setCategoryId, selectedCategory, setSubCategories, payload]);

  const formik = useFormik({
    initialValues: {
      category: categoryId || "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      category: Yup.string().required("Category is required"),
    }),
    onSubmit: (values) => {
      const progressSaveFlags = getOfferEditProgressSaveFlags({
        role,
        isEdit,
        isApprovedProp,
        editOfferData,
        offerData,
      });

      const currentCategoryId = values.category;
      const originalCategoryId =
        getCategoryId(payload?.category) ||
        getCategoryId(editOfferData?.category);
      const originalSubCategoryIds = normalizeSubCategoryIds(
        payload?.subCategory?.length ? payload.subCategory : editOfferData?.subCategory || []
      );
      const shouldPreserveSubCategory =
        String(currentCategoryId || "") === String(originalCategoryId || "");

      setPayloadData({
        category: values.category,
        subCategory: shouldPreserveSubCategory ? originalSubCategoryIds : [],
        ...progressSaveFlags,
      });
      setStepIndex(stepIndex + 1);
    },
  });

  const attemptNext = useCallback(async () => {
    formik.setTouched({ category: true });
    const validationErrors = await formik.validateForm();
    if (Object.keys(validationErrors || {}).length > 0) {
      scrollToFirstError(validationErrors, ["category"]);
      return;
    }
    formik.handleSubmit();
  }, [formik]);

  useEffect(() => {
    registerNextAction(() => attemptNext());
  }, [formik.values, registerNextAction, attemptNext]);

  useEffect(() => {
    if (validationIssue?.field !== "category") return;

    // B10 fix: Set both formik error (for native validation UX) AND the external
    // error state so the message persists even if formik reinitializes due to
    // the categoryId prop changing (enableReinitialize resets touched/errors).
    formik.setTouched({ category: true });
    formik.setFieldError("category", validationIssue.message);
    setExternalCategoryError(validationIssue.message);
    scrollToFirstError(
      { category: validationIssue.message },
      ["category"],
    );
    onValidationIssueHandled?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationIssue]);

  useEffect(() => {
    setIsNextDisabled(!formik.values.category);
  }, [formik.values.category, setIsNextDisabled]);

  const handleClick = (id) => {
    const cat = categories.find((c) => c._id === id);
    setSelectedCategory(cat);
    setCategoryId(id);
    formik.setFieldValue("category", id);
    // B10 fix: clear external error when user makes a selection
    setExternalCategoryError(null);
    setPayloadData({
      category: id,
      subCategory: [],
    });
    // NEW: Set subcategories from the selected category, or empty if none
    if (cat && Array.isArray(cat.subCategory)) {
      setSubCategories(cat.subCategory);
    } else {
      setSubCategories([]);
    }
  };

  const isCategoryClicked = (cat) => {
    return selectedCategory?._id === cat?._id;
  };

  const blueFilter = "invert(53%) sepia(95%) saturate(2070%) hue-rotate(167deg) brightness(95%) contrast(102%)";
  const greyFilter = "invert(88%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(103%) contrast(92%)";

  return (
    <div className="w-full flex flex-col items-center justify-start flex-grow">
      {/* Target Content: Spacing matched to reference image */}
      <div className="w-full max-w-[700px] mt-[30px] md:mt-[60px] relative">

        <div className="mb-6 md:mb-10 w-full !text-left xl:text-center">
          <h2 className="text-[22px] md:text-[32px] font-[600] text-[#303030] font-poppins !text-left xl:text-center w-full">
            Which <span className="text-[#00ADEF]">Category</span> Best Describes your Offer?
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5" data-field="category">
          {categories?.length > 0 &&
            categories?.map((cat) => {
              const clicked = isCategoryClicked(cat);
              return (
                <div
                  key={cat._id}
                  className={`flex flex-col items-start justify-start pt-6 p-5 h-[115px] rounded-[10px] border cursor-pointer transition-all duration-200 ${clicked
                    ? "border-2 border-[#00ADEF] bg-[#EBF9FE] shadow-sm"
                    : "border-[#E0E0E0] bg-white hover:border-[#BDBDBD]"
                    }`}
                  onClick={() => handleClick(cat?._id)}
                >
                  <div className="mb-[14px]">
                    <img
                      src={getCategoryIconSrc(cat?.image, cat?.title)}
                      alt={cat?.title}
                      className="w-[28px] h-[28px]"
                      style={clicked ? { filter: blueFilter } : { filter: greyFilter, opacity: 0.55 }}
                    />
                  </div>
                  <p className={`font-poppins font-[500] text-[14px] ${clicked ? "text-[#00ADEF]" : "text-[#303030]"}`}>
                    {cat?.title}
                  </p>
                </div>
              );
            })}
        </div>

        {/* B10 fix: show error from either formik touched state OR the external
            error state that survives formik reinitialisation */}
        {((formik.errors?.category && formik.touched?.category) || externalCategoryError) ? (
          <span className="text-red-600 sm:text-[10px] md:text-[12px] font-[600] ms-1 mt-3 block text-center">
            {externalCategoryError || "Category is required."}
          </span>
        ) : null}
      </div>
    </div>
  );
};

export default CategorySelection;
